import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './Dialog';
import { Button } from './Button';
import { AlertTriangle } from 'lucide-react';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  message?: string; // Alternative to description for backwards compatibility
  confirmLabel?: string;
  confirmText?: string; // Alternative to confirmLabel for backwards compatibility
  cancelLabel?: string;
  cancelText?: string; // Alternative to cancelLabel for backwards compatibility
  onConfirm: () => void;
  variant?: 'default' | 'destructive';
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  message,
  confirmLabel,
  confirmText,
  cancelLabel,
  cancelText,
  onConfirm,
  variant = 'default',
}) => {
  // Support both old and new prop names
  const descriptionText = description || message || '';
  const confirmButtonText = confirmLabel || confirmText || 'Confirm';
  const cancelButtonText = cancelLabel || cancelText || 'Cancel';
  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogHeader>
        <div className="flex items-center gap-3">
          {variant === 'destructive' && (
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-400" />
            </div>
          )}
          <DialogTitle>{title}</DialogTitle>
        </div>
      </DialogHeader>
      <DialogContent>
        <DialogDescription>{descriptionText}</DialogDescription>
      </DialogContent>
      <DialogFooter>
        <Button
          onClick={handleCancel}
          className="bg-gray-600 text-white border-gray-500 hover:bg-gray-500"
        >
          {cancelButtonText}
        </Button>
        <Button
          variant={variant === 'destructive' ? 'destructive' : 'default'}
          onClick={handleConfirm}
        >
          {confirmButtonText}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
